import type { ReactNode } from 'react';
import { Icon, Tooltip } from '@forecastify/ui';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT A FIGURE MEANS, WHERE THE FIGURE IS.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS SCREEN IS UNUSABLE WITHOUT THESE AND THAT IS NOT AN EXAGGERATION.** *CPI 0,94*,
 * *TCPI 1,12*, *VAC −374 118 Ft* — nobody outside a PMO reads those cold, and a dashboard whose
 * figures cannot be interrogated gets ignored or, worse, acted on wrongly. The mockup gives every
 * single tile, bar, footnote and column header a four-part explanation, and it is the most
 * carefully written thing in the whole prototype.
 *
 * The four parts each do a different job:
 *
 * | | |
 * |---|---|
 * | **name** | the term spelled out — *"CPI — cost performance index, azaz költséghatékonysági index"* |
 * | **what** | what it tells you, in a sentence |
 * | **calc** | the formula, so a figure can be checked rather than believed |
 * | **note** | how to read it — which direction is bad, and where the colour turns |
 *
 * ⚠️ **`state-rules.md` §10 still holds: the VALUE is readable without hovering.** This explains a
 * number that is already on the screen; it never carries the only copy of something.
 */
export interface FinanceTipText {
  readonly name: string;
  readonly what: string;
  readonly calc?: string;
  readonly note?: string;
}

/**
 * The inside of a hint — name, meaning, formula, how to read it. ⚠️ Exported so a `DataList`
 * column's `tip` renders exactly what `FinanceTip` does: one structure, so the registers and the
 * tiles above them cannot drift apart in how an explanation looks.
 */
export function financeTipContent(tip: FinanceTipText, calcLabel: string): ReactNode {
  return (
    /* ⚠️ The surface sets its own width and wrapping now — see `Tooltip`. What belongs here is
       the STRUCTURE of a hint: what it is, what it means, how it is worked out, what to watch
       for. ⚠️ `text-muted-foreground` rather than `opacity-80`: on the popover's white ground
       a faded near-black is grey with a wrong hue, and the token is the grey the rest of the
       product uses. */
    <span className="block text-left">
      <span className="block font-medium">{tip.name}</span>
      <span className="text-muted-foreground mt-0.5 block">{tip.what}</span>
      {tip.calc === undefined ? null : (
        <span className="mt-1.5 block tabular-nums">{`${calcLabel}: ${tip.calc}`}</span>
      )}
      {tip.note === undefined ? null : (
        <span className="text-muted-foreground mt-1.5 block">{tip.note}</span>
      )}
    </span>
  );
}

export function FinanceTip({
  tip,
  children,
  calcLabel,
}: {
  readonly tip: FinanceTipText;
  readonly children: ReactNode;
  /** Already translated — "Számítás". */
  readonly calcLabel: string;
}) {
  return (
    <Tooltip content={financeTipContent(tip, calcLabel)}>
      <span className="inline-flex items-center gap-1">
        {children}
        {/* ⚠️ `aria-hidden`: the icon is a hover affordance, and the tooltip itself is what a
            screen reader announces through the trigger's description. A second announced "info"
            per figure would make the page read as forty icons. */}
        <Icon name="circle-help" size={11} className="text-muted-foreground/60" aria-hidden />
      </span>
    </Tooltip>
  );
}
